import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { agodaApiService } from '../../services/agodaApiService';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  doc,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';

const AgodaBookingModal = ({ 
  property, 
  tripPlan, 
  isOpen, 
  onClose, 
  onBookingSuccess 
}) => {
  const { currentUser } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [bookingData, setBookingData] = useState({
    checkIn: '',
    checkOut: '',
    guests: 1,
    rooms: 1,
    guestInfo: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      nationality: 'TH'
    },
    specialRequests: '',
    paymentMethod: 'credit_card'
  });

  // Initialize booking data with trip plan info
  useEffect(() => {
    if (tripPlan && isOpen) {
      setBookingData(prev => ({
        ...prev,
        checkIn: tripPlan.tripDate || '',
        guests: tripPlan.participantCount || 1,
        guestInfo: {
          ...prev.guestInfo,
          firstName: currentUser?.displayName?.split(' ')[0] || '',
          lastName: currentUser?.displayName?.split(' ')[1] || '',
          email: currentUser?.email || ''
        }
      }));
    }
  }, [tripPlan, isOpen, currentUser]);

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setBookingData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setBookingData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const validateStep = (stepNumber) => {
    switch (stepNumber) {
      case 1:
        return bookingData.checkIn && bookingData.checkOut && bookingData.guests > 0;
      case 2:
        return bookingData.guestInfo.firstName && 
               bookingData.guestInfo.lastName && 
               bookingData.guestInfo.email && 
               bookingData.guestInfo.phone;
      case 3:
        return true; // Payment method selection
      default:
        return false;
    }
  };

  const calculateTotalPrice = () => {
    if (!property || !bookingData.checkIn || !bookingData.checkOut) return 0;
    
    const checkInDate = new Date(bookingData.checkIn);
    const checkOutDate = new Date(bookingData.checkOut);
    const nights = Math.ceil((checkOutDate - checkInDate) / (1000 * 60 * 60 * 24));
    
    const basePrice = property.price.amount * nights * bookingData.rooms;
    const tax = basePrice * 0.1; // 10% tax
    const serviceFee = basePrice * 0.05; // 5% service fee
    
    return {
      basePrice,
      tax,
      serviceFee,
      total: basePrice + tax + serviceFee
    };
  };

  const handleBookingSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      // Validate all steps
      if (!validateStep(1) || !validateStep(2) || !validateStep(3)) {
        throw new Error('Please complete all required fields');
      }

      // Create booking request
      const bookingRequest = {
        propertyId: property.id,
        checkIn: bookingData.checkIn,
        checkOut: bookingData.checkOut,
        guests: bookingData.guests,
        rooms: bookingData.rooms,
        guestInfo: bookingData.guestInfo,
        paymentInfo: {
          method: bookingData.paymentMethod,
          // In production, you would handle payment processing here
        },
        tripPlanId: tripPlan?.id,
        userId: currentUser.uid,
        userName: currentUser.displayName || currentUser.email
      };

      console.log('🚀 Creating Agoda booking request:', bookingRequest);

      // Call Agoda API
      const agodaResult = await agodaApiService.createBookingRequest(bookingRequest);
      
      if (!agodaResult.success) {
        throw new Error(agodaResult.error || 'Failed to create booking');
      }

      // Save booking to Firestore
      const bookingRecord = {
        ...bookingRequest,
        agodaBookingId: agodaResult.data.bookingId,
        status: 'confirmed',
        totalAmount: calculateTotalPrice().total,
        commissionAmount: calculateTotalPrice().total * (property.commissionRate || 0),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'agodaBookings'), bookingRecord);
      
      console.log('✅ Booking created successfully:', docRef.id);

      // Update trip plan checklist if applicable
      if (tripPlan?.id) {
        const tripRef = doc(db, 'tripPlans', tripPlan.id);
        await updateDoc(tripRef, {
          'checklist.prepareTrip.accommodationBooked': true,
          updatedAt: serverTimestamp()
        });
      }

      // Call success callback
      if (onBookingSuccess) {
        onBookingSuccess({
          bookingId: docRef.id,
          agodaBookingId: agodaResult.data.bookingId,
          totalAmount: calculateTotalPrice().total,
          property: property
        });
      }

      // Close modal
      onClose();

    } catch (error) {
      console.error('❌ Booking error:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (validateStep(step)) {
      setStep(step + 1);
      setError(null);
    }
  };

  const prevStep = () => {
    if (step > 1) {
      setStep(step - 1);
      setError(null);
    }
  };

  if (!isOpen) return null;

  const priceBreakdown = calculateTotalPrice();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Book Property</h2>
            <p className="text-gray-600">{property?.name}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ×
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            {[1, 2, 3].map((stepNumber) => (
              <div key={stepNumber} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step >= stepNumber 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-gray-200 text-gray-600'
                }`}>
                  {stepNumber}
                </div>
                <div className="ml-2 text-sm">
                  <div className={`font-medium ${
                    step >= stepNumber ? 'text-blue-600' : 'text-gray-500'
                  }`}>
                    {stepNumber === 1 && 'Dates & Guests'}
                    {stepNumber === 2 && 'Guest Information'}
                    {stepNumber === 3 && 'Payment & Confirm'}
                  </div>
                </div>
                {stepNumber < 3 && (
                  <div className={`w-16 h-0.5 mx-4 ${
                    step > stepNumber ? 'bg-blue-500' : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex">
              <svg className="h-5 w-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Booking Error</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Step Content */}
        <div className="p-6">
          {step === 1 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">Select Dates & Guests</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Check-in Date
                  </label>
                  <input
                    type="date"
                    value={bookingData.checkIn}
                    onChange={(e) => handleInputChange('checkIn', e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Check-out Date
                  </label>
                  <input
                    type="date"
                    value={bookingData.checkOut}
                    onChange={(e) => handleInputChange('checkOut', e.target.value)}
                    min={bookingData.checkIn || new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Number of Guests
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={bookingData.guests}
                    onChange={(e) => handleInputChange('guests', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Number of Rooms
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="5"
                    value={bookingData.rooms}
                    onChange={(e) => handleInputChange('rooms', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">Guest Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    First Name
                  </label>
                  <input
                    type="text"
                    value={bookingData.guestInfo.firstName}
                    onChange={(e) => handleInputChange('guestInfo.firstName', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={bookingData.guestInfo.lastName}
                    onChange={(e) => handleInputChange('guestInfo.lastName', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={bookingData.guestInfo.email}
                    onChange={(e) => handleInputChange('guestInfo.email', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={bookingData.guestInfo.phone}
                    onChange={(e) => handleInputChange('guestInfo.phone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Special Requests
                  </label>
                  <textarea
                    value={bookingData.specialRequests}
                    onChange={(e) => handleInputChange('specialRequests', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Any special requests or requirements..."
                  />
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">Payment & Confirmation</h3>
              
              {/* Price Breakdown */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-3">Price Breakdown</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Base Price ({bookingData.rooms} room(s))</span>
                    <span>฿{priceBreakdown.basePrice.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Tax (10%)</span>
                    <span>฿{priceBreakdown.tax.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Service Fee (5%)</span>
                    <span>฿{priceBreakdown.serviceFee.toLocaleString()}</span>
                  </div>
                  <div className="border-t pt-2 flex justify-between font-semibold">
                    <span>Total Amount</span>
                    <span>฿{priceBreakdown.total.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* Payment Method */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Payment Method
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="credit_card"
                      checked={bookingData.paymentMethod === 'credit_card'}
                      onChange={(e) => handleInputChange('paymentMethod', e.target.value)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Credit Card</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="bank_transfer"
                      checked={bookingData.paymentMethod === 'bank_transfer'}
                      onChange={(e) => handleInputChange('paymentMethod', e.target.value)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Bank Transfer</span>
                  </label>
                </div>
              </div>

              {/* Booking Summary */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-2">Booking Summary</h4>
                <div className="text-sm text-blue-800 space-y-1">
                  <div><strong>Property:</strong> {property?.name}</div>
                  <div><strong>Check-in:</strong> {new Date(bookingData.checkIn).toLocaleDateString('th-TH')}</div>
                  <div><strong>Check-out:</strong> {new Date(bookingData.checkOut).toLocaleDateString('th-TH')}</div>
                  <div><strong>Guests:</strong> {bookingData.guests} people</div>
                  <div><strong>Rooms:</strong> {bookingData.rooms} room(s)</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-6 border-t border-gray-200">
          <button
            onClick={prevStep}
            disabled={step === 1}
            className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 disabled:text-gray-400 rounded-lg"
          >
            Previous
          </button>
          
          <div className="flex gap-4">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg"
            >
              Cancel
            </button>
            
            {step < 3 ? (
              <button
                onClick={nextStep}
                disabled={!validateStep(step)}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 text-white rounded-lg"
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleBookingSubmit}
                disabled={loading}
                className="px-6 py-2 bg-green-500 hover:bg-green-600 disabled:bg-gray-400 text-white rounded-lg flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Processing...
                  </>
                ) : (
                  'Confirm Booking'
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgodaBookingModal;

